// BOMMaster.jsx
import React, { useEffect, useState } from "react";
import {
  FaEye,
  FaEdit,
  FaTrash,
  FaDownload,
  FaPlus,
  FaSort,
  FaSortUp,
  FaSortDown,
  FaSearch,
  FaChevronLeft,
  FaChevronRight,
} from "react-icons/fa";
import dayjs from "dayjs";
import Sidebar from "./Sidebar";
import { useScreenPermissions } from "./useScreenPermissions";
import BOMFormModal from "./BOMFormModal";
import BOMViewModal from "./BOMViewModal";
import { useNavigate } from "react-router-dom";

const LeftArrowIcon = (props) => (
  <svg {...props} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 320 512">
    <path
      fill="currentColor"
      d="M41.4 233.4c-12.5 12.5-12.5 32.8 0 45.3l160 160c12.5 12.5 32.8 12.5 45.3 0s12.5-32.8 0-45.3L109.3 256 246.6 118.6c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0l-160 160z"
    />
  </svg>
);

const RightArrowIcon = (props) => (
  <svg {...props} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 320 512">
    <path
      fill="currentColor"
      d="M278.6 233.4c12.5 12.5 12.5 32.8 0 45.3l-160 160c-12.5 12.5-32.8 12.5-45.3 0s-12.5-32.8 0-45.3L210.7 256 73.4 118.6c-12.5-12.5 12.5-32.8 0-45.3s32.8-12.5 45.3 0l160 160z"
    />
  </svg>
);

const API_URL = "/api";

// Helper function to get auth token
const getAuthToken = () => {
  return localStorage.getItem('authToken');
};

// Helper function to make authenticated API requests
const authenticatedFetch = async (url, options = {}) => {
  const token = getAuthToken();
  const headers = {
    'Content-Type': 'application/json',
    ...options.headers,
  };
  
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  
  return fetch(url, {
    ...options,
    headers,
  });
};

const BOMMaster = () => {
  const [boms, setBoms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [showView, setShowView] = useState(false);
  const [selectedBOM, setSelectedBOM] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const [isScrolled, setIsScrolled] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const navigate = useNavigate();
  const [status, setStatus] = useState("All");
  const [apiError, setApiError] = useState(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(true);
  const [formData, setFormData] = useState({
    users: [],
    products: [],
    uomOptions: [],
    nextBomNumber: '',
    nextDetailNumber: ''
  });
  
  // States for pagination
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 6;
  
  // State to force re-render of modals
  const [refreshKey, setRefreshKey] = useState(0);
  
  // Get user permissions
  const { hasPermission, hasFullAccess, loading: permissionsLoading } = useScreenPermissions();

  // THEME CSS VARIABLES (match VendorMaster exactly)
  useEffect(() => {
    const themeVars = {
      "--sidebar-line-color": "#e6efee",
      "--sidebar-bg": "#ffffff",
      "--accent-color": "#2b8f85",
      "--accent-hover": "#23756e",
      "--page-border-color": "#e6efee",
      "--text-primary": "#334155",
      "--text-muted": "#475569",
      "--text-muted-2": "#64748b",
      "--disabled-color": "#bcd8d5",
      "--sort-inactive": "#cddfe0",
      "--search-icon": "#cbd5da",
      // status colors (kept old names for Active/Inactive + added per-status colors)
      "--status-active-bg": "#e6f6f3",
      "--status-active-color": "#0f766e",
      "--status-inactive-bg": "#fafaf9",
      "--status-inactive-color": "#6b7280",
      // new specific colors per request:
      // New -> soft green
      "--status-new-bg": "#E6F9EC",
      "--status-new-color": "#065F46",
      // Development/Developed -> very light red (background) + readable red text
      "--status-developed-bg": "#fff1f0",   // very light red / pink
      "--status-developed-color": "#b91c1c",// readable red text
      // Certified -> very light yellow background + darker amber text
      "--status-certified-bg": "#fffbeb",   // very light yellow
      "--status-certified-color": "#92400e", // darker amber/brown for contrast
      // page & table header
      "--page-bg": "#EEF6F5",
      "--table-header-bg": "#EEF6F5",
      "--surface-1": "#ffffff",
      "--surface-2": "#f6fbfa",
      "--divider": "#e6efee",
      "--danger": "#ef4444"
    };

    Object.entries(themeVars).forEach(([key, value]) => {
      document.documentElement.style.setProperty(key, value);
    });

    const styleId = "bom-master-theme-overrides";
    if (!document.getElementById(styleId)) {
      const style = document.createElement("style");
      style.id = styleId;
      style.innerHTML = `
        /* primary button uses accent color */
        .primary-btn {
          background-color: var(--accent-color);
          color: #ffffff;
          
        }
        .primary-btn:hover { background-color: var(--accent-hover); }

        /* white secondary button (export) */
        .secondary-btn {
          background-color: var(--surface-1);
          color: var(--text-primary);
          border: 1px solid var(--page-border-color);
         
        }
        .secondary-btn:hover { background-color: #f8faf9; }

        /* status badges - Active/Inactive (existing) */
        .status-active {
          background-color: var(--status-active-bg);
          color: var(--status-active-color);
          border-radius: 9999px;
          padding: 4px 10px;
          font-size: 12px;
          display: inline-block;
          font-weight: 700;
        }
        .status-active:hover { filter: brightness(0.98); }

        .status-inactive {
          background-color: var(--status-inactive-bg);
          color: var(--status-inactive-color);
          border-radius: 9999px;
          padding: 4px 10px;
          font-size: 12px;
          display: inline-block;
          font-weight: 700;
        }
        .status-inactive:hover { filter: brightness(0.98); }

        .cust-status{
          border-radius: 9999px;
          padding: 4px 10px;
          font-size: 12px;
          display: inline-block;
          }

          .cust-status-in{
          border-radius: 6px;
          padding: 4px 10px;
          font-size: 12px;
          display: inline-block;
          }

        /* New green shade */
        .status-new {
          background-color: var(--status-new-bg);
          color: var(--status-new-color);
          border-radius: 9999px;
          padding: 4px 10px;
          font-size: 12px;
          display: inline-block;
          font-weight: 700;
          border: 1px solid rgba(6,95,70,0.08);
        }
        .status-new:hover { filter: brightness(0.98); }

        /* Development/Developed -> very light red background */
        .status-developed {
          background-color: var(--status-developed-bg);
          color: var(--status-developed-color);
          border-radius: 9999px;
          padding: 4px 10px;
          font-size: 12px;
          display: inline-block;
          font-weight: 700;
          border: 1px solid rgba(185,28,28,0.08);
        }
        .status-developed:hover { filter: brightness(0.98); }

        /* Certified -> very light yellow background */
        .status-certified {
          background-color: var(--status-certified-bg);
          color: var(--status-certified-color);
          border-radius: 9999px;
          padding: 4px 10px;
          font-size: 12px;
          display: inline-block;
          font-weight: 700;
          border: 1px solid rgba(146,64,14,0.08);
        }
        .status-certified:hover { filter: brightness(0.98); }

        /* icons that should use accent color */
        .accent-icon { color: var(--accent-color); }
        .accent-icon-hover:hover { color: var(--accent-hover); }

        /* sort arrow active color */
        .sort-active { color: var(--accent-color) !important; }
        .sort-inactive { color: var(--sort-inactive) !important; }

        /* pagination active */
        .page-active { background-color: var(--accent-color); color: #fff;  }
        .page-inactive { color: var(--accent-color); }
        .page-inactive:hover { background-color: #f7fbfa; }

        /* small helper so icon-only buttons still get hover transition */
        .icon-btn { transition: color .15s ease; }
      `;
      document.head.appendChild(style);
    }
  }, []);

  useEffect(() => {
    fetchBOMs();
    fetchFormData();
    // eslint-disable-next-line no-console
    console.log("BOMPage component is rendering");
    // refreshKey included in dependency above in original - keep behaviour
  }, [refreshKey]);

  // Add scroll listener for sticky header behavior
  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      setIsScrolled(scrollTop > 10);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const fetchBOMs = async () => {
    setLoading(true);
    setApiError(null);
    try {
      const res = await authenticatedFetch(`${API_URL}/bom`);
      
      if (!res.ok) {
        if (res.status === 401) {
          throw new Error("Authentication required. Please log in again.");
        }
        throw new Error(`HTTP error! Status: ${res.status}`);
      }
      
      const data = await res.json();
      // eslint-disable-next-line no-console
      console.log("BOMs API Response:", data);
      
      setBoms(data || []);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Error fetching BOMs:", error);
      setApiError(error.message);
      setBoms([]);
    }
    setLoading(false);
  };

  const fetchFormData = async () => {
    try {
      const res = await authenticatedFetch(`${API_URL}/bom/form-data`);
      
      if (!res.ok) {
        throw new Error(`HTTP error! Status: ${res.status}`);
      }
      
      const data = await res.json();
      // eslint-disable-next-line no-console
      console.log("Form Data Response:", data);
      
      setFormData({
        users: data.users || [],
        products: data.products || [],
        uomOptions: data.uomOptions || [],
        nextBomNumber: data.nextBomNumber || '',
        nextDetailNumber: data.nextDetailNumber || ''
      });
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Error fetching form data:", error);
      setApiError(error.message);
    }
  };

  const fetchBOMDetails = async (bomId) => {
    try {
      const res = await authenticatedFetch(`${API_URL}/bom/${bomId}`);
      
      if (!res.ok) {
        throw new Error(`HTTP error! Status: ${res.status}`);
      }
      
      const data = await res.json();
      // eslint-disable-next-line no-console
      console.log("BOM details response:", data);
      
      return data;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Error fetching BOM details:", error);
      throw error;
    }
  };

  const handleSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
    
    if (!Array.isArray(boms)) return;
    
    const sortedBOMs = [...boms].sort((a, b) => {
      if (a[key] === null) return 1;
      if (b[key] === null) return -1;
      
      // Handle date fields
      if (key === "CreatedAt" || key === "UpdatedAt") {
        return direction === "asc"
          ? new Date(a[key]) - new Date(b[key])
          : new Date(b[key]) - new Date(a[key]);
      }
      
      // Handle numeric fields
      if (key === "BOMQty") {
        const aNum = parseFloat(a[key]) || 0;
        const bNum = parseFloat(b[key]) || 0;
        return direction === "asc" ? aNum - bNum : bNum - aNum;
      }
      
      // Handle string fields
      if (a[key] < b[key]) return direction === "asc" ? -1 : 1;
      if (a[key] > b[key]) return direction === "asc" ? 1 : -1;
      return 0;
    });
    setBoms(sortedBOMs);
  };

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return <FaSort className="ml-1 w-3 h-3" style={{ color: 'var(--sort-inactive)' }} />;
    return sortConfig.direction === "asc" ? (
      <FaSortUp className="ml-1 w-3 h-3 sort-active" />
    ) : (
      <FaSortDown className="ml-1 w-3 h-3 sort-active" />
    );
  };

    const handleLogout = () => {
    navigate('/login');
  };

  const handleView = async (bom) => {
    try {
      const completeBOM = await fetchBOMDetails(bom.BOMID);
      setSelectedBOM(completeBOM);
      setShowView(true);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Error fetching BOM details for view:", error);
      alert("Error loading BOM details. Please try again.");
    }
  };

  const handleEdit = async (bom) => {
    try {
      const completeBOM = await fetchBOMDetails(bom.BOMID);
      setSelectedBOM(completeBOM);
      setIsEdit(true);
      setShowForm(true);
    } catch (error) {
      console.error("Error fetching BOM details for edit:", error);
      alert("Error loading BOM details for editing. Please try again.");
    }
  };

  const handleDelete = async (bom) => {
    if (!window.confirm("Are you sure you want to delete this BOM?"))
      return;
    try {
      const response = await authenticatedFetch(
        `${API_URL}/bom/${bom.BOMID}`,
        {
          method: "DELETE",
        }
      );
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to delete BOM");
      }
      fetchBOMs();
    } catch (error) {
      console.error("Error deleting BOM:", error);
      alert(
        error.message || "An error occurred while deleting the BOM"
      );
    }
  };

  const handleDownloadCSV = () => {
    if (!Array.isArray(boms) || boms.length === 0) {
      alert("No BOM data to export");
      return;
    }
    
    const headers = [
      "BOMID",
      "BOMType",
      "BOMName",
      "Status",
      "BOMQty",
      "Created By",
      "Created At",
      "Updated By",
      "Updated At",
    ];
    
    const rows = boms.map((bom) => [
      bom.BOMID || "",
      bom.BOMType || "",
      bom.BOMName || "",
      bom.Status || "",
      bom.BOMQty || "",
      bom.CreatedBy || "",
      bom.CreatedAt ? dayjs(bom.CreatedAt).format("YYYY-MM-DD HH:mm") : "",
      bom.UpdatedBy || "",
      bom.UpdatedAt ? dayjs(bom.UpdatedAt).format("YYYY-MM-DD HH:mm") : "",
    ]);
    
    const csv = [headers, ...rows].map((r) => r.join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "boms.csv";
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const handleCreate = () => {
    setSelectedBOM(null);
    setIsEdit(false);
    setShowForm(true);
  };

  const handleFormSubmit = async (response) => {
    setFormLoading(true);
    try {
      // Show success message
      alert(response.message || 'BOM saved successfully');
      
      // Close the form modal
      setShowForm(false);
      
      // Refresh the BOM list
      await fetchBOMs();
      
      // Force re-render of modals by updating refreshKey
      setRefreshKey(prev => prev + 1);
    } catch (error) {
      console.error("Error saving BOM:", error);
      alert(error.message || "An error occurred while saving the BOM");
    } finally {
      setFormLoading(false);
    }
  };

  const getStatusColor = (status) => {
    // Normalize status into string for comparisons
    const st = (status === true || status === 1 || status === "1") ? "Active"
             : (status === false || status === 0 || status === "0") ? "Inactive"
             : (typeof status === "string" ? status : String(status || ""));

    const key = st.toString().trim().toLowerCase();

    if (key === "new") return "status-new";
    if (key === "developed" || key === "development") return "status-developed";
    if (key === "certified") return "status-certified";
    if (key === "active") return "status-active";
    if (key === "inactive") return "status-inactive";
    // fallback
    return "status-inactive";
  };

  const getStatusText = (status) => {
    if (status === true || status === 1 || status === "1") return "Active";
    if (status === false || status === 0 || status === "0") return "Inactive";
    return status || "Unknown";
  };

  // Filter BOMs based on search term and filters
  const filteredBOMs = Array.isArray(boms) ? boms.filter((bom) => {
    if (!bom || typeof bom !== 'object') return false;
    
    const matchesSearch =
      searchTerm === "" ||
      (bom.BOMID && bom.BOMID.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (bom.BOMName && bom.BOMName.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (bom.BOMType && bom.BOMType.toLowerCase().includes(searchTerm.toLowerCase()));
      
    const bomStatus = getStatusText(bom.Status);
    const matchesStatus =
      status === "All" ||
      (status === "Active" && bomStatus === "Active") ||
      (status === "Inactive" && bomStatus === "Inactive");
      
    return matchesSearch && matchesStatus;
  }) : [];

  // Pagination logic
  const totalPages = Math.ceil(filteredBOMs.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const bomsForPage = filteredBOMs.slice(startIndex, endIndex);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const renderPaginationButtons = () => {
    const pages = [];
    const startPage = Math.max(1, currentPage - 2);
    const endPage = Math.min(totalPages, currentPage + 2);

    pages.push(
      <button
        key="prev"
        className={`p-2 transition-all duration-200 ${currentPage === 1 ? 'text-gray-400 cursor-not-allowed' : 'page-inactive'}`}
        onClick={() => handlePageChange(currentPage - 1)}
        disabled={currentPage === 1}
      >
        <LeftArrowIcon className="w-4 h-4" />
      </button>
    );

    for (let i = startPage; i <= endPage; i++) {
      const isActive = i === currentPage;
      pages.push(
        <button
          key={i}
          onClick={() => handlePageChange(i)}
          className={`px-4 py-1 font-medium transition-all duration-200 ${isActive ? 'page-active' : 'page-inactive'}`}
        >
          {i}
        </button>
      );
    }

    pages.push(
      <button
        key="next"
        className={`p-2 transition-all duration-200 ${currentPage === totalPages || totalPages === 0 ? 'text-gray-400 cursor-not-allowed' : 'page-inactive'}`}
        onClick={() => handlePageChange(currentPage + 1)}
        disabled={currentPage === totalPages || totalPages === 0}
      >
        <RightArrowIcon className="w-4 h-4" />
      </button>
    );

    return pages;
  };

  // Calculate stats with safety checks
  const totalBOMs = Array.isArray(boms) ? boms.length : 0;
  const activeBOMs = Array.isArray(boms) 
    ? boms.filter(b => getStatusText(b.Status) === "Active").length 
    : 0;
  const inactiveBOMs = Array.isArray(boms) 
    ? boms.filter(b => getStatusText(b.Status) === "Inactive").length 
    : 0;
  const thisMonthBOMs = Array.isArray(boms) 
    ? boms.filter(b => {
        const createdDate = new Date(b.CreatedAt);
        const now = new Date();
        return (
          createdDate.getMonth() === now.getMonth() &&
          createdDate.getFullYear() === now.getFullYear()
        );
      }).length 
    : 0;

  // Check permissions for actions
  const canCreate = hasFullAccess || hasPermission('BOM_MASTER', 'CanCreate');
  const canView = hasFullAccess || hasPermission('BOM_MASTER', 'CanView');
  const canEdit = hasFullAccess || hasPermission('BOM_MASTER', 'CanEdit');
  const canDelete = hasFullAccess || hasPermission('BOM_MASTER', 'CanDelete');

  if (permissionsLoading) {
    return (
      <div className="flex h-screen items-center justify-center" style={{ backgroundColor: 'var(--page-bg)' }}>
        <div className="text-center">
          <div className="animate-spin h-12 w-12 border-t-4 border-b-4" style={{ borderColor: 'var(--accent-color)' }}></div>
          <p className="mt-4 text-lg font-medium" style={{ color: 'var(--text-muted)' }}>Loading permissions...</p>
        </div>
      </div>
    );
  }

  // Theme object passed to Sidebar so it matches this page's accent/profile color
  const sidebarTheme = {
    text: 'var(--text-primary, #334155)',
    muted: 'var(--text-muted, #475569)',
    border: 'var(--page-border-color, #e6efee)',
    sidebarBorder: 'var(--sidebar-line-color, #e6efee)',
    background: 'var(--sidebar-bg, #ffffff)',
    pageBackground: 'var(--page-bg, #EEF6F5)',
    activeBg: 'var(--accent-color, #2b8f85)',
    profileBg: 'var(--accent-color, #2b8f85)',
    activeText: '#ffffff',
    accent: 'var(--accent-color, #2b8f85)',
    accentHover: 'var(--accent-hover, #23756e)',
    hoverGray: 'rgba(0,0,0,0.04)',
    iconColor: 'var(--accent-color, #2b8f85)',
    sidebarBg: 'var(--sidebar-bg, #ffffff)',
  };

  return (
    <div className="min-h-screen flex" style={{ backgroundColor: 'var(--page-bg)' }}>
      <Sidebar collapsed={sidebarCollapsed} onToggle={setSidebarCollapsed} theme={sidebarTheme} onLogout={handleLogout} />
      <main className={`flex-1 p-6 lg:p-8 overflow-hidden transition-all duration-300 ${sidebarCollapsed ? 'lg:ml-[90px]' : 'lg:ml-[280px]'}
`}>

        {/* Header Section */}
        <header className="mb-8 flex flex-col md:flex-row justify-between items-start md:items-center gap-4 border-b border-gray-300 pb-4">
          <div>
            <h1 className="text-3xl font-bold" style={{ color: 'var(--text-primary)' }}>Bill of Materials (BOM)</h1>
            <p className="mt-1" style={{ color: 'var(--text-muted)' }}>Manage your product bills of materials</p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={handleDownloadCSV}
              className="flex items-center gap-2 px-5 py-2 secondary-btn font-medium transition-all duration-200 shadow-sm"
            >
              <FaDownload className="h-4 w-4" />
              Export
            </button>
            {canCreate && (
              <button
                onClick={handleCreate}
                className="flex items-center gap-2 px-5 py-2 primary-btn font-medium hover:shadow transition-all duration-200 shadow-sm rounded-none"
              >
                <FaPlus className="h-4 w-4" />
                Create New
              </button>
            )}
          </div>
        </header>

        {/* API Error Display */}
        {apiError && (
          <div className="mb-6 p-4" style={{ backgroundColor: '#ffeef0', border: '1px solid #fecaca', color: '#b91c1c' }}>
            <p>Error loading BOMs: {apiError}</p>
            <div className="mt-2 flex gap-2">
              <button 
                onClick={fetchBOMs}
                className="px-3 py-1 primary-btn"
                style={{ backgroundColor: 'var(--danger)', color: '#fff' }}
              >
                Retry
              </button>
              {apiError.includes("Authentication required") && (
                <button 
                  onClick={() => { window.location.href = '/login'; }}
                  className="px-3 py-1"
                  style={{ backgroundColor: 'var(--text-primary)', color: '#fff', borderRadius: 6 }}
                >
                  Login
                </button>
              )}
            </div>
          </div>
        )}

        {/* Stats Cards */}
        <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <div className="p-5 bg-[color:var(--surface-1)] border border-gray-300 shadow-md transition-shadow duration-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-semibold" style={{ color: 'var(--text-muted)' }}>Total BOMs</p>
                <p className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>{totalBOMs}</p>
              </div>
              <div className="w-10 h-10 flex items-center justify-center" style={{ backgroundColor: 'var(--surface-2)', color: 'var(--text-primary)', borderRadius: 6 }}>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
              </div>
            </div>
          </div>

          <div className="p-5 bg-[color:var(--surface-1)] border border-gray-300 shadow-md transition-shadow duration-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-semibold" style={{ color: 'var(--text-muted)' }}>Active BOMs</p>
                <p className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>{activeBOMs}</p>
              </div>
              <div className="w-10 h-10 flex items-center justify-center" style={{ backgroundColor: 'var(--status-active-bg)', color: 'var(--status-active-color)', borderRadius: 6 }}>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
            </div>
          </div>

          <div className="p-5 bg-[color:var(--surface-1)] border border-gray-300 shadow-md transition-shadow duration-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-semibold" style={{ color: 'var(--text-muted)' }}>Inactive BOMs</p>
                <p className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>{inactiveBOMs}</p>
              </div>
              <div className="w-10 h-10 flex items-center justify-center" style={{ backgroundColor: 'var(--status-inactive-bg)', color: 'var(--status-inactive-color)', borderRadius: 6 }}>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
            </div>
          </div>

          <div className="p-5 bg-[color:var(--surface-1)] border border-gray-300 shadow-md transition-shadow duration-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-semibold" style={{ color: 'var(--text-muted)' }}>This Month</p>
                <p className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>{thisMonthBOMs}</p>
              </div>
              <div className="w-10 h-10 flex items-center justify-center" style={{ backgroundColor: 'var(--surface-2)', color: 'var(--text-primary)', borderRadius: 6 }}>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2" />
                </svg>
              </div>
            </div>
          </div>
        </section>

        {/* Search and Filters */}
        <section className="p-5 bg-[color:var(--surface-1)] border border-gray-300 shadow-md mb-6">
          <div className="flex flex-col md:flex-row gap-3 items-end">
            <div className="flex-1">
              <label className="block text-sm font-semibold mb-1" style={{ color: 'var(--text-muted)' }}>Search BOMs</label>
              <div className="relative mt-2">
                <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2" style={{ color: 'var(--search-icon)' }} />
                <input
                  type="text"
                  placeholder="Search by BOM ID, name or type..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full px-4 py-2 pl-10"
                  style={{ backgroundColor: 'var(--surface-1)', border: `1px solid var(--divider)`, color: 'var(--text-primary)' }}
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-semibold mb-1" style={{ color: 'var(--text-muted)' }}>Status</label>
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                className="px-4 py-2"
                style={{ backgroundColor: 'var(--surface-1)', border: `1px solid var(--divider)`, color: 'var(--text-primary)' }}
              >
                <option value="All">All</option>
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
              </select>
            </div>
            <button 
              className="px-5 py-2 primary-btn font-medium rounded-none"
              onClick={() => setCurrentPage(1)}
            >
              Search
            </button>
          </div>
        </section>

        {/* BOMs Table */}
        <section className="bg-[color:var(--surface-1)] border border-gray-300 shadow-md">
          <div className="flex justify-between items-center p-4 border-b border-gray-300" >
            <h2 className="text-xl font-semibold" style={{ color: 'var(--text-primary)' }}>BOM Summary</h2>
            <div className="flex gap-2 items-center">
              <div className="flex items-center gap-2" style={{ color: 'var(--text-muted)' }}>
                {renderPaginationButtons()}
              </div>
            </div>
          </div>

          {loading ? (
            <div className="p-8 text-center" style={{ color: 'var(--text-muted-2)' }}>Loading BOMs...</div>
          ) : bomsForPage.length === 0 ? (
            <div className="p-8 text-center" style={{ color: 'var(--text-muted-2)' }}>{apiError ? "Failed to load BOMs" : "No BOMs found."}</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead style={{ backgroundColor: 'var(--table-header-bg)' }} className="text-gray-600 text-xs font-semibold uppercase text-left">
                  <tr>
                    <th
                      className={`px-6 py-3 text-left cursor-pointer ${isScrolled ? "shadow" : ""}`}
                      onClick={() => handleSort("BOMID")}
                    >
                      <div className="flex items-center">
                        BOM ID {getSortIcon("BOMID")}
                      </div>
                    </th>
                    <th
                      className={`px-6 py-3 text-left cursor-pointer ${isScrolled ? "shadow" : ""}`}
                      onClick={() => handleSort("BOMName")}
                    >
                      <div className="flex items-center">
                        BOM Name {getSortIcon("BOMName")}
                      </div>
                    </th>
                    <th
                      className={`px-6 py-3 text-left cursor-pointer ${isScrolled ? "shadow" : ""}`}
                      onClick={() => handleSort("BOMType")}
                    >
                      <div className="flex items-center">
                        BOM Type {getSortIcon("BOMType")}
                      </div>
                    </th>
                    <th
                      className={`px-6 py-3 text-left cursor-pointer ${isScrolled ? "shadow" : ""}`}
                      onClick={() => handleSort("BOMQty")}
                    >
                      <div className="flex items-center">
                        Quantity {getSortIcon("BOMQty")}
                      </div>
                    </th>
                    <th
                      className={`px-6 py-3 text-left cursor-pointer ${isScrolled ? "shadow" : ""}`}
                      onClick={() => handleSort("Status")}
                    >
                      <div className="flex items-center">
                        Status {getSortIcon("Status")}
                      </div>
                    </th>
                    <th
                      className={`px-6 py-3 text-left cursor-pointer ${isScrolled ? "shadow" : ""}`}
                      onClick={() => handleSort("CreatedAt")}
                    >
                      <div className="flex items-center">
                        Created Date {getSortIcon("CreatedAt")}
                      </div>
                    </th>
                    <th
                      className={`px-6 py-3 text-left cursor-pointer ${isScrolled ? "shadow" : ""}`}
                      onClick={() => handleSort("UpdatedAt")}
                    >
                      <div className="flex items-center">
                        Last Updated {getSortIcon("UpdatedAt")}
                      </div>
                    </th>
                    <th className="px-6 py-3 text-center">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y" style={{ borderTop: '1px solid var(--divider)' }}>
                  {bomsForPage.map((bom, idx) => (
                    <tr
                      key={bom.BOMID || idx}
                      className={`hover:bg-[color:var(--surface-2)] transition-colors duration-200 bg-[color:var(--surface-1)]`}
                    >
                      <td className="px-6 py-3 font-mono" style={{ color: 'var(--text-primary)' , whiteSpace: 'nowrap' }}>
                        <p
                          className={`transition-all duration-200 ${!canView ? 'pointer-events-none' : 'cursor-pointer'}`}
                          onClick={() => canView && handleView(bom)}
                        >
                          {bom.BOMID || "N/A"}
                        </p>
                      </td>
                      <td className="px-6 py-3" style={{ color: 'var(--text-primary)' }}>{bom.BOMName || "N/A"}</td>
                      <td className="px-6 py-3" style={{ color: 'var(--text-muted)' }}>{bom.BOMType || "N/A"}</td>
                      <td className="px-6 py-3" style={{ color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>{bom.BOMQty ? parseFloat(bom.BOMQty).toFixed(2) : "N/A"}</td>
                      <td className="px-6 py-3">
                        <span className={getStatusColor(bom.Status)}>
                          {getStatusText(bom.Status)}
                        </span>
                      </td>
                      <td className="px-6 py-3" style={{ color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>{bom.CreatedAt ? dayjs(bom.CreatedAt).format("DD MMM YYYY") : "N/A"}</td>
                      <td className="px-6 py-3 whitespace-nowrap">
                        {bom.UpdatedAt ? (
                          <div className="flex flex-col leading-tight">
                            <span style={{ color: 'var(--text-primary)' }}>{dayjs(bom.UpdatedAt).format("DD MMM YYYY")}</span>
                            <span className="text-xs" style={{ color: 'var(--text-muted-2)' }}>{dayjs(bom.UpdatedAt).format("HH:mm")}</span>
                          </div>
                        ) : "N/A"}
                      </td>
                      <td className="px-6 py-3 flex gap-2 justify-center items-center">
                        {canView && (
                          <button title="View" className="p-2 rounded-full icon-btn" onClick={() => handleView(bom)} style={{ color: 'var(--accent-color)' }}>
                            <FaEye />
                          </button>
                        )}
                        {canEdit && (
                          <button title="Edit" className="p-2 rounded-full icon-btn" onClick={() => handleEdit(bom)} style={{ color: 'var(--accent-color)' }}>
                            <FaEdit />
                          </button>
                        )}
                        {canDelete && (
                          <button title="Delete" className="p-2 rounded-full icon-btn" onClick={() => handleDelete(bom)} style={{ color: 'var(--danger)' }}>
                            <FaTrash />
                          </button>
                        )}
                        <button title="Download CSV" className="p-2 rounded-full icon-btn" onClick={handleDownloadCSV} style={{ color: 'var(--text-muted)' }}>
                          <FaDownload />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        {/* Modals */}
        <BOMFormModal
          open={showForm}
          onClose={() => setShowForm(false)}
          onSubmit={handleFormSubmit}
          initialData={selectedBOM}
          isEdit={isEdit}
          loading={formLoading}
          formData={formData}
          key={`form-${refreshKey}`}
        />
        <BOMViewModal
          open={showView}
          onClose={() => setShowView(false)}
          bom={selectedBOM}
          key={`view-${refreshKey}`}
        />
      </main>
    </div>
  );
};

export default BOMMaster;
